// CollaborationTodosQuadrant — Bottom-left quadrant: To Do Items with CRUD
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckSquare, Plus, Circle, CheckCircle, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react'
import { useCollaborationFilter } from '../../hooks/useCollaborationFilter'
import type { CollaborationTodo, DetailItem } from '../../types/collaboration'
import type { TodoStatus, TodoPriority } from '../../types'

const PAGE_SIZE = 10

const STATUS_BADGE: Record<TodoStatus, string> = {
  open: 'bg-gray-500/20 text-gray-600',
  in_progress: 'bg-blue-500/20 text-blue-700',
  completed: 'bg-green-500/20 text-green-700',
  cancelled: 'bg-red-500/20 text-red-600',
  suggested: 'bg-purple-500/20 text-purple-700',
  // Real status written by whatnext/handler.py for AI/workflow-suggested todos.
  pending: 'bg-indigo-500/20 text-indigo-700',
}

const PRIORITY_BADGE: Record<TodoPriority, string> = {
  low: 'bg-gray-500/20 text-gray-500',
  medium: 'bg-yellow-500/20 text-yellow-700',
  high: 'bg-orange-500/20 text-orange-700',
  urgent: 'bg-red-500/20 text-red-700',
}

interface Props {
  onDetailClick: (item: DetailItem) => void
}

export default function CollaborationTodosQuadrant({ onDetailClick }: Props) {
  const { filteredTodos } = useCollaborationFilter()
  const navigate = useNavigate()
  const [page, setPage] = useState(0)

  const totalPages = Math.ceil(filteredTodos.length / PAGE_SIZE)
  const safePage = Math.min(page, Math.max(0, totalPages - 1))
  const pagedTodos = filteredTodos.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)

  // Reset page when filtered items change
  useEffect(() => { setPage(0) }, [filteredTodos.length])

  return (
    <div className="light-card flex flex-col" style={{ maxHeight: '420px' }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-sky-200/50">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-iflow-600" />
          <h3 className="text-sm font-semibold text-gray-900">To Do Items</h3>
          <span className="px-1.5 py-0.5 text-xs rounded-full bg-sky-500/10 text-gray-600 font-medium">{filteredTodos.length}</span>
        </div>
        <button
          onClick={() => navigate('/crm/todos')}
          title="Add a to-do in My To-Dos"
          className="flex items-center gap-1 px-2 py-1 text-xs bg-iflow-700 text-white rounded-lg hover:bg-iflow-800"
        >
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {filteredTodos.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No to-dos match the current filter.</p>
        ) : (
          <div className="space-y-1">
            {pagedTodos.map((todo) => (
              <TodoRow key={todo.id} todo={todo} onDetailClick={onDetailClick} />
            ))}
          </div>
        )}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 text-xs text-gray-500">
          <button
            disabled={safePage === 0}
            onClick={() => setPage(p => p - 1)}
            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3 h-3" /> Prev
          </button>
          <span>{safePage + 1} of {totalPages}</span>
          <button
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Next <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  )
}

function TodoRow({ todo, onDetailClick }: { todo: CollaborationTodo; onDetailClick: (item: DetailItem) => void }) {
  const isComplete = todo.status === 'completed'
  const isSuggestion = todo.status === 'suggested'
  return (
    <div
      onClick={() => onDetailClick({ type: 'todo', item: todo })}
      className={`flex items-start gap-2.5 px-3 py-2 rounded-lg hover:bg-sky-500/5 cursor-pointer ${isSuggestion ? 'border-l-3 border-purple-400' : ''}`}
    >
      <span className="mt-0.5 flex-shrink-0 text-gray-500" aria-hidden="true">
        {isComplete ? <CheckCircle className="w-4 h-4 text-green-500" /> : isSuggestion ? <Sparkles className="w-4 h-4 text-purple-500" /> : <Circle className="w-4 h-4" />}
      </span>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${isComplete ? 'line-through text-gray-500' : 'text-gray-900'}`}>{todo.title}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_BADGE[todo.status]}`}>
            {todo.status.replace('_', ' ')}
          </span>
          <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PRIORITY_BADGE[todo.priority]}`}>
            {todo.priority}
          </span>
          {isSuggestion && (
            <span
              className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-amber-500/15 text-amber-700 border border-amber-200"
              title="Suggested items are previews. Actionable workflows (compose message, open form) activate in Phase 2."
            >
              Preview — Phase 2
            </span>
          )}
          {todo.dueDate && (
            <span className="text-xs text-gray-500">Due {todo.dueDate}</span>
          )}
        </div>
      </div>
    </div>
  )
}
